import { TranslateService } from '@ngx-translate/core';
import { Component, EventEmitter, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AddressService } from 'src/app/core/service/address.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { AddressValidationRequest } from './address-validator';
import { Code } from 'src/app/core/model/dictionary';
import { HelperService } from 'src/app/core/service/helper.service';

@Component({
  standalone: false,
  selector: 'app-address-validator',
  templateUrl: './address-validator.component.html',
  styleUrls: ['./address-validator.component.scss']
})
export class AddressValidatorComponent
{
  @Output() public changeAddress = new EventEmitter<any>();
  @Output() public cancelling = new EventEmitter<any>();
  @Output() public save = new EventEmitter<any>();

  public addressForm: any;
  public dialogOpened = false;
  public addressCompleted = false;
  public showRecommendation = false;
  public missingComponentTypes = [];
  public unconfirmedComponentTypes = [];
  public previousResponseId = null;

  public currentAddressLine1: string;
  public currentAddressLine2: string;
  public currentAddressLine3: string;

  public recommendedGeoCode: any;
  public recommendedAddress: any;
  public recommendedAddressLine1: string;
  public recommendedAddressLine2: string;
  public recommendedAddressLine3: string;

  public textErrorStreet = '';
  public textErrorStreetNumber = '';
  public textErrorCity = '';
  public textErrorState = '';
  public textErrorZip = '';
  public textErrorPremise = '';
  public textPointOfInterest = '';
  public textErrorZipSuffix = '';
  public autoSave = false;
  public countries: Code[] = [];
  public addressIndex: null;

  constructor(
    private translate: TranslateService,
    private settingService: SettingService,
    private messageHandlerService: MessageHandlerService,
    private addressService: AddressService,
    private helperService: HelperService)
  {
    this.helperService.getStatesCountriesFromMultipleSources().subscribe({
      next: (responseList) =>
      {
        this.countries = responseList[1];
      }
    });
  }

  public get addressConfirmation(): UntypedFormControl
  {
    return this.addressForm.get('addressConfirmation');
  }

  /**
   * Execute entry point for address validation. If the addressApiKey is null, the execute will returns immediatly to continue with save.
   *
   * @param addressLine1 Address line 1 provided
   * @param addressLine2 Address line 2 provided
   * @param addressCity City provided
   * @param stateCode State Code provided
   * @param countryCode Country Code provided
   * @param zipCode Zip Code provided
   */
  public execute(autoSave: boolean, addressLine1: string = null, addressLine2: string = null, addressCity: string = null,
    stateCode: string = null, countryCode: string = null, zipCode: string = null, addressIndex = null): void
  {
    this.autoSave = autoSave;
    this.addressIndex = addressIndex;
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        // If address API is enable, proceed with address validation
        if (sessionSetting.addressAPIEnabled)
        {
          this.translate.get([
            'ADDRESS-ERROR-STREET',
            'ADDRESS-ERROR-STREET-NUMBER',
            'ADDRESS-ERROR-CITY',
            'ADDRESS-ERROR-STATE',
            'ADDRESS-ERROR-ZIP',
            'ADDRESS-ERROR-PREMISE',
            'ADDRESS-ERROR-ZIP-SUFFIX',
            'ADDRESS-ERROR-POINT-OF-INTEREST']).subscribe({
              next: (res) =>
              {
                // Set text error messages used for failed address
                this.textErrorStreet = res['ADDRESS-ERROR-STREET'];
                this.textErrorStreetNumber = res['ADDRESS-ERROR-STREET-NUMBER'];
                this.textErrorCity = res['ADDRESS-ERROR-CITY'];
                this.textErrorState = res['ADDRESS-ERROR-STATE'];
                this.textErrorZip = res['ADDRESS-ERROR-ZIP'];
                this.textErrorPremise = res['ADDRESS-ERROR-PREMISE'];
                this.textErrorZipSuffix = res['ADDRESS-ERROR-ZIP-SUFFIX'];
                this.textPointOfInterest = res['ADDRESS-ERROR-POINT-OF-INTEREST'];

                // Build form and address lines
                this.addressForm = new UntypedFormGroup({ addressConfirmation: new UntypedFormControl(null, Validators.required) });

                // Formatting current address to show to line1, line2 and line3.
                // Not currently showing country since Channels is using 3 characters ISO (USA) and Address API 2 (US)
                this.currentAddressLine1 = addressLine1 ? addressLine1 : '';
                this.currentAddressLine2 = addressLine2 ? addressLine2 : '';
                this.currentAddressLine3 = '';
                if (addressCity) { this.currentAddressLine3 += addressCity; }
                if (stateCode) { this.currentAddressLine3 += this.currentAddressLine3 !== '' ? `, ${stateCode}` : stateCode; }
                if (zipCode) { this.currentAddressLine3 += this.currentAddressLine3 !== '' ? ` ${zipCode}` : zipCode; }
                if (countryCode) { this.currentAddressLine3 += this.currentAddressLine3 !== '' ? ` ${countryCode}` : countryCode; }

                // Build the address validation request
                const addressValidation: AddressValidationRequest =
                {
                  line1: addressLine1,
                  line2: addressLine2,
                  city: addressCity,
                  state: stateCode,
                  postalCode: zipCode,
                  country: countryCode != null ? this.countries.find(x => x.code === countryCode).alternateCode : null,
                  previousResponseId: this.previousResponseId
                };

                // Initialize invalid component types list
                this.missingComponentTypes = [];
                this.unconfirmedComponentTypes = [];
                this.recommendedGeoCode = null;
                this.recommendedAddress = null;
                this.recommendedAddressLine1 = '';
                this.recommendedAddressLine2 = '';
                this.recommendedAddressLine3 = '';

                // User local variable and set global variable once
                let recommendedAddressLine1 = '';
                let recommendedAddressLine2 = '';
                let recommendedAddressLine3 = '';

                // Validate the address
                this.addressService.validateAddress(addressValidation).subscribe({
                  next: (response) =>
                  {
                    // If no response, the service may have an error or restrictions.
                    if (response === null)
                    {
                      this.messageHandlerService.displayWarning('VALIDATE-ADDRESS-FEATURE-UNAVAILABLE');
                      if (autoSave)
                      {
                        this.save.emit();
                      }
                      return;
                    }

                    // Formatting recommended address
                    if (response.result !== null && response.result.address !== null && response.result.address.postalAddress !== null)
                    {
                      this.previousResponseId = response.responseId;    // Keep the responseId for setting the previousResponseId
                      this.missingComponentTypes = response.result.address.missingComponentTypes || [];
                      this.unconfirmedComponentTypes = response.result.address.unconfirmedComponentTypes || [];
                      this.recommendedAddress = response.result.address.postalAddress;
                      this.recommendedGeoCode = response.result.geocode !== null ? response.result.geocode : null;

                      // Formatting result address to show to line1, line2 and line3.
                      if (response.result.address.addressComponents !== null)
                      {
                        // We use the address components to re-build line1 and line2 because postalAddress.addressLines have line2 injected
                        let street = '';
                        let route = '';
                        response.result.address.addressComponents.forEach(addr =>
                        {
                          switch (addr.componentType)
                          {
                            case 'street_number':
                              street = addr.componentName.text;
                              break;
                            case 'route':
                              route = addr.componentName.text;
                              break;
                            case 'subpremise':
                              recommendedAddressLine2 = addr.componentName.text !== '' ?
                                addr.componentName.text.charAt(0).toUpperCase() + addr.componentName.text.slice(1) : '';
                              break;
                          }
                        });
                        recommendedAddressLine1 = street;
                        recommendedAddressLine1 += (route !== '') ? (recommendedAddressLine1 !== '' ? ` ${route}` : route) : '';
                      }

                      // Recommended Address Line3
                      if (response.result.address.postalAddress.locality)
                      {
                        recommendedAddressLine3 += response.result.address.postalAddress.locality;
                      }
                      if (response.result.address.postalAddress.administrativeArea)
                      {
                        recommendedAddressLine3 += recommendedAddressLine3 !== '' ?
                          `, ${response.result.address.postalAddress.administrativeArea}` :
                          response.result.address.postalAddress.administrativeArea;
                      }
                      if (response.result.address.postalAddress.postalCode)
                      {
                        recommendedAddressLine3 += recommendedAddressLine3 !== '' ?
                          ` ${response.result.address.postalAddress.postalCode}` : response.result.address.postalAddress.postalCode;
                      }
                      if (response.result.address.postalAddress.regionCode)
                      {
                        const lookupCountryCode = this.countries.find(x => x.alternateCode ===
                          response.result.address.postalAddress.regionCode);
                        if (lookupCountryCode)
                        {
                          recommendedAddressLine3 += recommendedAddressLine3 !== '' ?
                            ` ${lookupCountryCode.code}` : lookupCountryCode.code;
                        }
                      }
                    }

                    // Set address completed if there is no missing or unconfirmed types
                    this.addressCompleted = this.missingComponentTypes.length === 0 && this.unconfirmedComponentTypes.length === 0;

                    // Set showRecommendation only if address are different
                    this.showRecommendation = (this.currentAddressLine1 !== recommendedAddressLine1 ||
                      this.currentAddressLine2 !== recommendedAddressLine2 ||
                      this.currentAddressLine3 !== recommendedAddressLine3) &&
                      (recommendedAddressLine1 + recommendedAddressLine2 + recommendedAddressLine3 !== '');

                    // Automatically set to original if we can't recommend an address
                    this.addressConfirmation.setValue(!this.showRecommendation ? 'original' : null);
                    this.dialogOpened = this.showRecommendation || !this.addressCompleted || !autoSave;
                    this.recommendedAddressLine1 = recommendedAddressLine1;
                    this.recommendedAddressLine2 = recommendedAddressLine2;
                    this.recommendedAddressLine3 = recommendedAddressLine3;

                    // Address is the same and no errors therefore we continue with save automatically.
                    if (!this.dialogOpened && autoSave)
                    {
                      this.save.emit();
                    }
                  },
                  error: (errorResponse) =>
                  {
                    this.messageHandlerService.displayError(errorResponse);
                    if (autoSave)
                    {
                      this.save.emit();
                    }
                  }
                });
              }
            });
        }
        else if (autoSave)
        {
          this.save.emit();
        }
      }
    });
  }

  /**
   * Cancel event. This will stop the save process.
   */
  public cancelDialog(): void
  {
    this.dialogOpened = false;
    this.cancelling.emit();
  }

  /**
   * Address confirmation has been selected. If recommended, the changeAddress event is emitted to allow overriding the address before save.
   */
  public confirm(): void
  {
    this.dialogOpened = false;

    const address: any = {};
    address.autoSave = this.autoSave;
    address.confirmation = this.addressConfirmation.value;
    address.addressIndex = this.addressIndex;

    if (this.addressConfirmation.value === 'recommended')
    {
      address.line1 = this.recommendedAddressLine1;
      address.line2 = this.recommendedAddressLine2;
      address.postalAddress = this.recommendedAddress;
      address.geocode = this.recommendedGeoCode;
      this.changeAddress.emit(address);
    }
    else
    {
      this.changeAddress.emit(address);
    }
  }
}
